// Merchant view-mode toggle — per-device persistence + cross-tab sync.
//
// A merchant account owns BOTH shop inventory and (optionally) a personal
// cellar; the toggle lets them flip context without changing identity.
// localStorage is per-device on purpose: a merchant might use a tablet
// for shop work and a phone for personal browsing — those choices
// shouldn't synchronize across devices the way profile.themeMode does.
//
// Cross-tab sync within the same device works two ways:
//   - storage events (browser-native) fire when ANOTHER tab writes the
//     same localStorage key
//   - a custom `vinarai-view-mode-change` event covers the same-tab case
//     where storage events don't fire
//
// Default is 'shop' so existing merchant accounts behave exactly as
// before until they explicitly switch. Non-merchant accounts ignore
// this entirely (App.jsx only consults viewMode when accountType is
// 'merchant').

import { useEffect, useState } from 'react'

const KEY   = 'vinarai_view_mode_v1'
const EVENT = 'vinarai-view-mode-change'

function readRaw() {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'personal' ? 'personal' : 'shop'  // default + sanitize
  } catch {
    return 'shop'
  }
}

export function setViewMode(mode) {
  const next = mode === 'personal' ? 'personal' : 'shop'
  try {
    localStorage.setItem(KEY, next)
    // Fire the same-tab event so listeners in this tab notice immediately
    // (storage events only fire in OTHER tabs per the spec).
    window.dispatchEvent(new CustomEvent(EVENT, { detail: next }))
  } catch {
    /* localStorage disabled (private mode, full storage) — silent. The
       toggle still functions in-memory for the current session via the
       useViewMode hook's local state, but won't persist. */
  }
}

// React hook — subscribes to both storage events (cross-tab) and the
// custom event (same-tab). Returns the current view mode + a setter
// that updates state, persists, and broadcasts.
export function useViewMode() {
  const [mode, setMode] = useState(readRaw)

  useEffect(() => {
    const onStorage = (e) => { if (e.key === KEY) setMode(e.newValue === 'personal' ? 'personal' : 'shop') }
    const onLocal   = (e) => { setMode(e.detail === 'personal' ? 'personal' : 'shop') }
    window.addEventListener('storage', onStorage)
    window.addEventListener(EVENT, onLocal)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(EVENT, onLocal)
    }
  }, [])

  return [mode, setViewMode]
}

// ── Active org id (for multi-org users) ─────────────────────────────────────
//
// A staff member at three different shops needs to remember WHICH shop
// they're acting as right now. localStorage-backed, per-device, same
// pattern as viewMode above. Only consulted when viewMode === 'shop'.

const KEY_ORG   = 'vinarai_active_org_v1'
const EVENT_ORG = 'vinarai-active-org-change'

function readActiveOrgRaw() {
  try {
    const v = localStorage.getItem(KEY_ORG)
    return v && typeof v === 'string' ? v : null
  } catch { return null }
}

export function setActiveOrgId(orgId) {
  try {
    if (orgId) localStorage.setItem(KEY_ORG, orgId)
    else       localStorage.removeItem(KEY_ORG)
    window.dispatchEvent(new CustomEvent(EVENT_ORG, { detail: orgId || null }))
  } catch { /* silent — same as setViewMode */ }
}

export function useActiveOrgId() {
  const [orgId, setOrgId] = useState(readActiveOrgRaw)
  useEffect(() => {
    const onStorage = (e) => { if (e.key === KEY_ORG) setOrgId(e.newValue || null) }
    const onLocal   = (e) => { setOrgId(e.detail || null) }
    window.addEventListener('storage', onStorage)
    window.addEventListener(EVENT_ORG, onLocal)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(EVENT_ORG, onLocal)
    }
  }, [])
  return [orgId, setActiveOrgId]
}

// ── Combined "switch context" helper ────────────────────────────────────────
//
// Atomic helper used by the UserMenu mode switcher. Sets viewMode +
// activeOrgId in one shot so listeners only see one consistent state
// transition.

export function switchToContext(ctx) {
  if (ctx === 'personal' || ctx?.type === 'personal') {
    setViewMode('personal')
    setActiveOrgId(null)
    return
  }
  if (ctx?.type === 'merchant' && ctx.orgId) {
    setActiveOrgId(ctx.orgId)
    setViewMode('shop')
  }
}
